"use client";

import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { getFirebaseConfig } from "@/lib/auth/firebase";
import { LoggerProvider as LoggerContextProvider } from "@/lib/observability/LoggerContext";
import { getQueryClient } from "@/lib/query/client";
import { Wrapper } from "@googlemaps/react-wrapper";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ReactQueryStreamedHydration } from "@tanstack/react-query-next-experimental";
import { APIProvider } from "@vis.gl/react-google-maps";
import {
  DatadogProvider as AnalyticsDatadogProvider,
  setAnalyticsProvider,
} from "@workspace/analytics";
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
} from "firebase/analytics";
import { getApps, initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { PropsWithChildren, useEffect } from "react";

export function FirebaseProvider({ children }: PropsWithChildren) {
  const firebaseConfig = getFirebaseConfig();
  // Prevent duplicate initialization in dev or double-render scenarios
  if (!getApps().length) {
    const app = initializeApp(firebaseConfig);

    isAnalyticsSupported().then((supported) => supported && getAnalytics());

    if (process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === "true") {
      connectAuthEmulator(getAuth(app), "http://localhost:9099");
    }
  }

  return <>{children}</>;
}

export function GoogleMapsProvider({ children }: PropsWithChildren) {
  const MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  if (!MAPS_API_KEY) {
    throw new Error("NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is not defined");
  }

  return (
    <APIProvider apiKey={MAPS_API_KEY}>
      <Wrapper apiKey={MAPS_API_KEY} libraries={["places"]}>
        {children}
      </Wrapper>
    </APIProvider>
  );
}

export function LoggerProvider({ children }: PropsWithChildren) {
  const { user } = useAuth();
  return (
    <LoggerContextProvider
      serviceName="Local"
      userId={user?.uid}
      organizationId={undefined}
    >
      {children}
    </LoggerContextProvider>
  );
}

// Usually disabled for development
export function DatadogProvider({ children }: PropsWithChildren) {
  useEffect(() => {
    const appId = process.env.NEXT_PUBLIC_DATADOG_APP_ID;
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
    const apiUrl = process.env.NEXT_PUBLIC_GRPC_URL || "http://localhost:8080";

    if (!appId || !clientToken) {
      return;
    }

    // Check if Datadog is already initialized to prevent multiple initializations
    if (
      typeof window !== "undefined" &&
      (window as { DD_RUM?: unknown }).DD_RUM
    ) {
      return;
    }

    const provider = new AnalyticsDatadogProvider();
    provider.init({
      applicationId: appId,
      clientToken,
      site: process.env.NEXT_PUBLIC_DATADOG_SITE,
      service: "web",
      env: process.env.NODE_ENV,
      allowedTracingUrls: [apiUrl],
    });
    setAnalyticsProvider(provider);
  }, []); // Empty dependency array to run only once

  return <>{children}</>;
}

export function AnalyticsProvider({ children }: PropsWithChildren) {
  return <DatadogProvider>{children}</DatadogProvider>;
}

export function QueryProviders(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryStreamedHydration>
        {props.children}
      </ReactQueryStreamedHydration>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
